"use server";

import { redirect } from "next/navigation";
import { gql } from "@/lib/lecturehub/client";
import { getOptionalUser, verifySession } from "@/lib/lecturehub/dal";
import { M_PROFILE_EDIT } from "@/lib/lecturehub/operations";
import { setSignupPrefill } from "@/lib/newsletter-prefill";
import { langPath, type Lang } from "@/lib/i18n";
import { safeRedirect } from "@/lib/routes/canonicalize";

/** M_PROFILE_EDIT selects `{ id name surname email newsletter }` — not the
 *  rest of MyAccount (D34). Mirrors profil/uredi/actions.ts, which cannot be
 *  imported from here: it is a "use server" module. */
type ProfileEditData = {
  profileEdit: {
    id: string;
    name: string | null;
    surname: string | null;
    email: string;
    newsletter: boolean | null;
  };
};

export type NewsletterSignupState = { status: "idle" | "subscribed" | "error" };

/**
 * The Newsletter block's `form` layout submit. Two honest paths, because the
 * portal API has no anonymous subscribe mutation:
 *
 * - GUEST: stash name+email in the short-lived prefill cookie (see
 *   lib/newsletter-prefill.ts — never query params) and redirect to the
 *   authored destination (the register page, which prefills from the cookie
 *   and pre-ticks its newsletter opt-in). The redirect target is authored
 *   (cta.href) and clamped to an internal path by safeRedirect.
 * - SIGNED-IN: a real one-click subscribe — `profileEdit(newsletter: true)`
 *   with the account's own name/surname (both are required by the mutation).
 *   Idempotent for someone already subscribed.
 */
export async function newsletterSignupAction(
  _prev: NewsletterSignupState,
  formData: FormData,
): Promise<NewsletterSignupState> {
  const lang = (String(formData.get("lang") ?? "SI") === "EN" ? "EN" : "SI") as Lang;
  const name = String(formData.get("name") ?? "").trim();
  const email = String(formData.get("email") ?? "").trim();
  const href = safeRedirect(String(formData.get("href") ?? ""), "/register");

  const user = await getOptionalUser();
  if (!user) {
    await setSignupPrefill({ name, email });
    redirect(langPath(lang, href));
  }

  // Server Actions run independently of the page — per the DAL rule the
  // side-effecting path re-verifies for itself.
  const { user: me } = await verifySession();
  try {
    await gql<ProfileEditData>(M_PROFILE_EDIT, {
      name: me.name ?? name,
      surname: me.surname ?? "",
      newsletter: true,
    });
    return { status: "subscribed" };
  } catch {
    return { status: "error" };
  }
}
